import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { Coin, Gift, ArrowDown, ArrowUp, Sparkle, Ticket } from "@phosphor-icons/react";

const KIND_META = {
  bonus: { icon: <Sparkle size={14} weight="fill" />, label: "Bonus", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  topup: { icon: <ArrowUp size={14} weight="bold" />, label: "Top-up", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  coupon: { icon: <Gift size={14} weight="fill" />, label: "Coupon", tone: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
  debit: { icon: <ArrowDown size={14} weight="bold" />, label: "Debit", tone: "text-red-700 bg-red-50 border-red-200" },
  refund: { icon: <ArrowUp size={14} weight="bold" />, label: "Refund", tone: "text-blue-700 bg-blue-50 border-blue-200" },
};

export default function Wallet() {
  const [state, setState] = useState({ balance: 0, signup_bonus: 500, recent_transactions: [] });
  const [txAll, setTxAll] = useState([]);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [w, t] = await Promise.all([
        api.get("/wallet/me"),
        api.get("/wallet/me/transactions"),
      ]);
      setState(w.data);
      setTxAll(t.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const redeem = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { toast.error("Enter a code"); return; }
    setRedeeming(true);
    try {
      const r = await api.post("/wallet/redeem", { code: c });
      toast.success(`+${r.data.credited} credits redeemed!`);
      setCode("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Redeem failed"); }
    finally { setRedeeming(false); }
  };

  const stats = {
    earned: txAll.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0),
    spent: Math.abs(txAll.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0)),
    txCount: txAll.length,
  };

  return (
    <div>
      <PageHeader
        overline="Your credits"
        title="Wallet"
        subtitle="Track your credit balance, redeem codes, and view every transaction. Credits are used to unlock paid exams and certificate downloads."
      />

      {/* Balance card */}
      <div className="grid md:grid-cols-3 gap-0 border border-zinc-200 mb-6">
        <div className="md:col-span-2 p-8 bg-zinc-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <Coin size={280} weight="fill" style={{ position: "absolute", right: -60, top: -60 }} />
          </div>
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-70 font-semibold inline-flex items-center gap-1.5">
              <Coin size={11} weight="fill" /> Current balance
            </div>
            <div className="font-display text-6xl font-bold tabular mt-3" data-testid="wallet-balance">
              {state.balance.toLocaleString()}
              <span className="text-lg opacity-60 ml-2 font-normal">credits</span>
            </div>
            <div className="text-xs opacity-70 mt-3 max-w-md">
              Sign-up bonus of {state.signup_bonus.toLocaleString()} credits given to every student. Redeem codes from your admin for more.
            </div>
          </div>
        </div>
        <div className="grid grid-rows-3">
          <MiniStat label="Earned" value={stats.earned.toLocaleString()} icon={<ArrowUp size={12} weight="bold" />} tone="text-emerald-700" />
          <MiniStat label="Spent" value={stats.spent.toLocaleString()} icon={<ArrowDown size={12} weight="bold" />} tone="text-red-700" />
          <MiniStat label="Transactions" value={stats.txCount.toLocaleString()} tone="text-zinc-900" />
        </div>
      </div>

      {/* Redeem */}
      <div className="border border-zinc-200 p-6 mb-6" data-testid="wallet-redeem">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1 font-semibold inline-flex items-center gap-1.5">
              <Ticket size={11} weight="fill" /> Redeem a code
            </div>
            <div className="text-sm text-zinc-700">Enter a coupon code from your institution admin to top up credits.</div>
          </div>
          <div className="flex gap-2 flex-1 max-w-md">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD-1234"
              className="flex-1 border border-zinc-300 px-3 py-2.5 text-sm font-mono uppercase focus:border-zinc-900 focus:outline-none"
              onKeyDown={(e) => { if (e.key === "Enter") redeem(); }}
              data-testid="wallet-redeem-input"
            />
            <button
              onClick={redeem}
              disabled={redeeming || !code.trim()}
              className="bg-zinc-900 text-white px-5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="wallet-redeem-btn"
            >
              <Gift size={14} weight="fill" /> {redeeming ? "Redeeming…" : "Redeem"}
            </button>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="border border-zinc-200">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Transactions</div>
          <div className="text-xs text-zinc-500">{txAll.length} total</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Loading…</div>
        ) : txAll.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="wallet-empty">
            No transactions yet.
          </div>
        ) : (
          <div className="divide-y divide-zinc-200" data-testid="wallet-tx-list">
            {txAll.map((t) => {
              const meta = KIND_META[t.kind] || { icon: null, label: t.kind, tone: "text-zinc-700 bg-zinc-50 border-zinc-200" };
              return (
                <div key={t.tx_id} className="px-6 py-4 flex items-center gap-4" data-testid={`wallet-tx-${t.tx_id}`}>
                  <div className={`w-9 h-9 flex items-center justify-center border ${meta.tone}`}>
                    {meta.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.reason}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-3">
                      <span className="uppercase tracking-wider">{meta.label}</span>
                      <span className="text-zinc-300">·</span>
                      <span>{new Date(t.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-mono-tab text-lg font-bold tabular ${t.amount >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {t.amount >= 0 ? "+" : ""}{t.amount.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">bal {t.balance_after.toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon, tone }) {
  return (
    <div className="p-4 border-b border-l border-zinc-200 last:border-b-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 inline-flex items-center gap-1">{icon} {label}</div>
      <div className={`font-display text-2xl font-bold tabular ${tone || "text-zinc-900"}`}>{value}</div>
    </div>
  );
}
